import {
  IDownloadSession,
  IDownloadSessionProgressMessage,
} from '../../models/DownloadSession';

export interface DownloadSessionState {
  sessions: Record<number, IDownloadSession>;
  selectedSessionId: number | null;
  panelVisible: boolean;
}

let pendingIdCounter = -1;

export default {
  state: {
    sessions: {} as Record<number, IDownloadSession>,
    selectedSessionId: null as number | null,
    panelVisible: false,
  } as DownloadSessionState,

  mutations: {
    CREATE_PENDING_DOWNLOAD_SESSION(
      state: DownloadSessionState,
      payload: {
        uid: number;
        fileType: number;
        startDate: string;
        endDate: string;
        message?: string;
      }
    ) {
      // Remove any existing session (pending or real) for this UID so the
      // dropdown never shows a stale entry alongside the new pending one.
      for (const key of Object.keys(state.sessions)) {
        const sid = Number(key);
        if (state.sessions[sid].uid === payload.uid) {
          if (state.selectedSessionId === sid) {
            state.selectedSessionId = null;
          }
          delete state.sessions[sid];
        }
      }

      const tempId = pendingIdCounter--;
      state.sessions[tempId] = {
        downloadSessionId: tempId,
        uid: payload.uid,
        fileType: payload.fileType,
        state: 'T',
        percentComplete: 0,
        latestPassageTimestamp: '',
        passageCount: 0,
        startDate: payload.startDate,
        endDate: payload.endDate,
        passages: [],
        message: payload.message,
      };
      if (state.selectedSessionId === null) {
        state.selectedSessionId = tempId;
      }
      state.panelVisible = true;
    },

    UPSERT_DOWNLOAD_SESSION(
      state: DownloadSessionState,
      payload: IDownloadSessionProgressMessage
    ) {
      const id = payload.downloadSessionId;

      // Real session arrived — remove any pending placeholder for this UID.
      // Carry over the pending session's message (if any) to the real session.
      let pendingMessage: string | undefined;
      if (id > 0) {
        for (const key of Object.keys(state.sessions)) {
          const sid = Number(key);
          if (sid < 0 && state.sessions[sid].uid === payload.uid) {
            pendingMessage = state.sessions[sid].message;
            if (state.selectedSessionId === sid) {
              state.selectedSessionId = id;
            }
            delete state.sessions[sid];
          }
        }
      }

      const existing = state.sessions[id];
      // EN-1506: the retry flag and error describe this state; a new state
      // (or a new session) starts unchecked.
      const sameState = existing?.state === payload.state;
      state.sessions[id] = {
        downloadSessionId: id,
        uid: payload.uid,
        fileType: payload.fileType ?? 0,
        state: payload.state,
        percentComplete: payload.percentComplete ?? 0,
        latestPassageTimestamp: payload.latestPassageTimestamp ?? '',
        passageCount: payload.passageCount ?? 0,
        startDate: payload.startDate,
        endDate: payload.endDate,
        passages: existing?.passages ?? [],
        passagesLoadedState: existing?.passagesLoadedState,
        message: existing?.message ?? pendingMessage,
        retryable: sameState ? existing?.retryable : undefined,
        retryError: sameState ? existing?.retryError : undefined,
      };
      // Auto-select the first session when none is selected
      if (state.selectedSessionId === null) {
        state.selectedSessionId = id;
      }
      // A session exists, so the panel should be visible.
      state.panelVisible = true;
    },

    REMOVE_DOWNLOAD_SESSION(state: DownloadSessionState, sessionId: number) {
      delete state.sessions[sessionId];
      if (state.selectedSessionId === sessionId) {
        const ids = Object.keys(state.sessions).map(Number);
        state.selectedSessionId = ids.length > 0 ? ids[0] : null;
      }
      if (Object.keys(state.sessions).length === 0) {
        state.panelVisible = false;
      }
    },

    SET_DOWNLOAD_SESSION_SELECTED(
      state: DownloadSessionState,
      sessionId: number | null
    ) {
      state.selectedSessionId = sessionId;
    },

    SET_DOWNLOAD_SESSION_PANEL_VISIBLE(
      state: DownloadSessionState,
      visible: boolean
    ) {
      state.panelVisible = visible;
    },

    CLEAR_ALL_DOWNLOAD_SESSIONS(state: DownloadSessionState) {
      state.sessions = {};
      state.selectedSessionId = null;
      state.panelVisible = false;
    },

    SET_DOWNLOAD_SESSION_RETRY_ERROR(
      state: DownloadSessionState,
      payload: { sessionId: number; message?: string }
    ) {
      const session = state.sessions[payload.sessionId];
      if (session) {
        session.retryError = payload.message;
      }
    },

    SET_DOWNLOAD_SESSION_RETRYABLE(
      state: DownloadSessionState,
      payload: { sessionId: number; retryable: boolean | undefined }
    ) {
      const session = state.sessions[payload.sessionId];
      if (session) {
        session.retryable = payload.retryable;
      }
    },

    SET_DOWNLOAD_SESSION_PASSAGES(
      state: DownloadSessionState,
      payload: {
        sessionId: number;
        passages: any[];
        loadedState?: IDownloadSession['state'];
      }
    ) {
      const session = state.sessions[payload.sessionId];
      if (session) {
        session.passages = payload.passages;
        session.passagesLoadedState = payload.loadedState;
      }
    },
  },

  actions: {
    // Commits the session state and serves as the subscribeAction signal
    // for DownloadSessionPanel's debounced passage fetch.
    downloadSessionStateReceived(
      context: any,
      payload: IDownloadSessionProgressMessage
    ) {
      context.commit('UPSERT_DOWNLOAD_SESSION', payload);
    },

    createPendingDownloadSession(
      context: any,
      payload: {
        uid: number;
        fileType: number;
        startDate: string;
        endDate: string;
      }
    ) {
      context.commit('CREATE_PENDING_DOWNLOAD_SESSION', payload);
    },

    removeDownloadSession(context: any, sessionId: number) {
      context.commit('REMOVE_DOWNLOAD_SESSION', sessionId);
    },
  },

  getters: {
    activeDownloadSessionList(state: DownloadSessionState): IDownloadSession[] {
      return Object.values(state.sessions).sort(
        (a, b) => a.downloadSessionId - b.downloadSessionId
      );
    },

    selectedDownloadSession(
      state: DownloadSessionState
    ): IDownloadSession | null {
      if (state.selectedSessionId === null) return null;
      return state.sessions[state.selectedSessionId] ?? null;
    },
  },
};
